import { useApolloClient, useMutation } from '@apollo/client';
import { BoundObjectType } from '@generated/graphql';
import { useCallback, useState } from 'react';
import {
  ACTIVE_PROPOSAL_STATES,
  type ModelChangeProposal,
  type ModelChangeProposalTransition,
  type TransitionModelChangeProposalInput,
} from './modelChangeProposal.types';
import { graphQLErrorsOf } from './modelChangeProposalErrors';
import type {
  CreateModelChangeProposalPayloadArgs,
  UpdateModelChangeProposalPayloadArgs,
} from './modelChangeProposalPayload';
import {
  buildCreateModelChangeProposalInput,
  buildUpdateModelChangeProposalInput,
} from './modelChangeProposalPayload';
import {
  CREATE_MODEL_CHANGE_PROPOSAL,
  FETCH_HOUSEHOLD_MODEL_CHANGE_PROPOSALS,
  TRANSITION_MODEL_CHANGE_PROPOSAL,
  UPDATE_MODEL_CHANGE_PROPOSAL,
} from './queries';

type FetchProposalsData = {
  fetchModelChangeProposals?: {
    modelChangeProposals?: ModelChangeProposal[] | null;
  } | null;
};

type CreateProposalData = {
  createModelChangeProposal?: {
    modelChangeProposal?: ModelChangeProposal | null;
  } | null;
};

type UpdateProposalData = {
  updateModelChangeProposal?: {
    modelChangeProposal?: ModelChangeProposal | null;
  } | null;
};

type TransitionProposalData = {
  transitionModelChangeProposal?: {
    modelChangeProposal?: ModelChangeProposal | null;
  } | null;
};

/**
 * Thrown when the proposal the workflow would write to is past editing.
 *
 * Only a draft can be changed, and a household cannot have a second proposal
 * beside the one it has, so there is nothing the step can do on its own: the
 * advisor has to see the proposal through or cancel it. Carries the proposal so
 * the step can word itself from the state rather than assume one.
 */
export class ProposalNotEditableError extends Error {
  readonly proposal: ModelChangeProposal;

  constructor(proposal: ModelChangeProposal) {
    super(`Model change proposal ${proposal.id} is ${proposal.state}`);
    this.name = 'ProposalNotEditableError';
    this.proposal = proposal;
  }
}

/**
 * Thrown when a proposal is asked to go out for signature without a record of
 * what the household holds today.
 *
 * product-service refuses it at that point, and by then the advisor is at the
 * end of the workflow with nothing to correct, so the check runs here instead.
 */
export class ProposalNotSubmittableError extends Error {
  readonly proposal: ModelChangeProposal;

  constructor(proposal: ModelChangeProposal) {
    super(
      `Model change proposal ${proposal.id} has no current allocations to be signed against`,
    );
    this.name = 'ProposalNotSubmittableError';
    this.proposal = proposal;
  }
}

const isNotAccessible = (error: unknown): boolean =>
  graphQLErrorsOf(error).some(
    (graphQLError) => graphQLError.extensions?.code === 'NOT_ACCESSIBLE',
  );

/**
 * The refusal product-service gives when the household's model already has a
 * proposal open against it. Matched on the message because the code it comes
 * with, `NOT_ACCESSIBLE`, is the same one a household customized from a
 * different model gets — and that one is a real conflict the advisor has to
 * resolve, not something to write over.
 */
const isProposalAlreadyOpen = (error: unknown): boolean =>
  graphQLErrorsOf(error).some(
    (graphQLError) =>
      graphQLError.extensions?.code === 'NOT_ACCESSIBLE' &&
      /active proposal already exists/i.test(graphQLError.message),
  );

/**
 * The proposal a workflow works against.
 *
 * A household can only have one open at a time, so saving has to cope with
 * finding one already there: a workflow resumed after the browser was closed,
 * an advance replayed after a failed save, or a second advisor part way through
 * the same change. Which proposal may be written to is left to product-service
 * to say rather than worked out here, because it is the only one that knows
 * which model the household's copy was taken from — writing to the wrong one
 * would put this change onto a proposal raised against a different model.
 */
export const useModelChangeProposal = () => {
  const graphqlClient = useApolloClient();
  const [createProposal, { loading: creating }] =
    useMutation<CreateProposalData>(CREATE_MODEL_CHANGE_PROPOSAL);
  const [updateProposal, { loading: updating }] =
    useMutation<UpdateProposalData>(UPDATE_MODEL_CHANGE_PROPOSAL);
  const [transitionProposal, { loading: transitioning }] =
    useMutation<TransitionProposalData>(TRANSITION_MODEL_CHANGE_PROPOSAL);
  const [finding, setFinding] = useState(false);

  /**
   * The household's open proposal, read past the cache: another advisor may
   * have raised or signed one since this workflow started, and a remembered
   * answer would have the step act on a proposal that has moved on.
   */
  const findOpenProposal = useCallback(
    async (householdId: string): Promise<ModelChangeProposal | undefined> => {
      setFinding(true);
      try {
        const { data } = await graphqlClient.query<FetchProposalsData>({
          query: FETCH_HOUSEHOLD_MODEL_CHANGE_PROPOSALS,
          variables: {
            input: {
              filter: {
                objectType: BoundObjectType.Household,
                objectId: householdId,
                states: ACTIVE_PROPOSAL_STATES,
              },
              // Spelled the way the API spells it. Anything else is refused
              // outright as a field the input has never heard of.
              pagination: { offSet: 0, perPage: 1 },
            },
            skipErrorHandler: true,
          },
          fetchPolicy: 'network-only',
        });

        return data?.fetchModelChangeProposals?.modelChangeProposals?.[0];
      } finally {
        setFinding(false);
      }
    },
    [graphqlClient],
  );

  const writeToProposal = useCallback(
    async ({
      modelChangeProposalId,
      proposed,
      current,
      includeModelName,
    }: UpdateModelChangeProposalPayloadArgs): Promise<ModelChangeProposal> => {
      const { data } = await updateProposal({
        variables: {
          input: buildUpdateModelChangeProposalInput({
            modelChangeProposalId,
            proposed,
            current,
            includeModelName,
          }),
          skipErrorHandler: true,
        },
      });

      const proposal = data?.updateModelChangeProposal?.modelChangeProposal;
      if (!proposal) {
        throw new Error('updateModelChangeProposal returned no proposal');
      }

      return proposal;
    },
    [updateProposal],
  );

  /**
   * Puts the change on a proposal: onto the one the workflow already opened, or
   * onto a new one.
   *
   * Creating is tried before looking, so that the household's open proposal is
   * only written to when product-service has said the change belongs on it.
   * Looking first and writing to whatever came back would step around the check
   * it makes that the household's copy came from the model being changed.
   */
  const saveProposal = useCallback(
    async ({
      proposed,
      current,
      householdId,
      organizationId,
      modelChangeProposalId,
      includeModelName,
    }: CreateModelChangeProposalPayloadArgs & {
      /** The proposal this workflow opened earlier, if it opened one. */
      modelChangeProposalId?: string;
    }): Promise<ModelChangeProposal> => {
      if (modelChangeProposalId) {
        try {
          return await writeToProposal({
            modelChangeProposalId,
            proposed,
            current,
            includeModelName,
          });
        } catch (error) {
          // Signed or cancelled while the advisor was still editing.
          if (!isNotAccessible(error)) throw error;

          const open = await findOpenProposal(householdId);
          if (open?.id === modelChangeProposalId) {
            throw new ProposalNotEditableError(open);
          }
          throw error;
        }
      }

      try {
        const { data } = await createProposal({
          variables: {
            input: buildCreateModelChangeProposalInput({
              proposed,
              current,
              householdId,
              organizationId,
              includeModelName,
            }),
            skipErrorHandler: true,
          },
        });

        const proposal = data?.createModelChangeProposal?.modelChangeProposal;
        if (!proposal) {
          throw new Error('createModelChangeProposal returned no proposal');
        }

        return proposal;
      } catch (error) {
        if (!isProposalAlreadyOpen(error)) throw error;

        const open = await findOpenProposal(householdId);
        // Cancelled in the moment between the refusal and the look.
        if (!open) throw error;
        if (open.state !== 'DRAFT') throw new ProposalNotEditableError(open);

        return writeToProposal({
          modelChangeProposalId: open.id,
          proposed,
          current,
        });
      }
    },
    [findOpenProposal, createProposal, writeToProposal],
  );

  const transition = useCallback(
    async (
      modelChangeProposalId: string,
      transitionName: ModelChangeProposalTransition,
    ): Promise<ModelChangeProposal> => {
      const input: TransitionModelChangeProposalInput = {
        modelChangeProposalId,
        transition: transitionName,
      };
      const { data } = await transitionProposal({
        variables: { input, skipErrorHandler: true },
      });

      const proposal = data?.transitionModelChangeProposal?.modelChangeProposal;
      if (!proposal) {
        throw new Error('transitionModelChangeProposal returned no proposal');
      }

      return proposal;
    },
    [transitionProposal],
  );

  return {
    findOpenProposal,
    saveProposal,
    /**
     * Sends the proposal out for signature, leaving it awaiting one.
     *
     * Takes the proposal rather than its id so that what the household holds
     * today can be checked before it goes. Sending is its own permission in
     * product-service, so an advisor allowed to draft may still be refused
     * here — worth saying plainly rather than as a failed save.
     */
    submitProposal: (proposal: ModelChangeProposal) => {
      if (!proposal.currentAllocations?.length) {
        throw new ProposalNotSubmittableError(proposal);
      }
      return transition(proposal.id, 'submit');
    },
    /** Abandons the proposal, freeing the household to be changed again. */
    cancelProposal: (modelChangeProposalId: string) =>
      transition(modelChangeProposalId, 'cancel'),
    loading: finding || creating || updating || transitioning,
  };
};
